import { CODE, DATATYPE, UNIT } from 'domains/product-attribute/constants'
import TimeUtil from 'utils/time-util'
import * as Yup from 'yup'


// Provide services for Product Attribute
const ProductAttributeService = {

  /**
   * Generate Formik's Initial value.
   *
   * ! Move it to Seller Inbound domain if the merchant does not use this
   * @param {array} attributes - Collection of ProductAttributes
   * @param {object} initialValues - Initial value
   */
  buildFormikInitialValue: (attributes, initialValues = {}) => {

    let generatedValues = {}
    attributes.forEach((attribute) => {

      if (attribute.allow_multiple_values) {
        generatedValues[attribute.code] = []
      }
      else {
        generatedValues[attribute.code] = ''
      }
    })

    // * initialValue will overwrite generatedValues
    return {
      ...generatedValues,
      ...initialValues
    }

  },

  /**
 * Generate validation attributes schema for Formik.
 * ! might need to remove later since we have already buildFormikDatatypeValidationSchmea
 * @param {object[]} attributes - Selected Product Attributes.
 * @return {object} Yup validation schema
 */
  buildFormikValidationSchema: (attributes) => {
    let dynamicSchema = {}

    if (attributes) {
      attributes.forEach((attribute) => {
        // If set to array since the formik values would be array for multiple input
        if (attribute.allow_multiple_values) {
          dynamicSchema[attribute.code] = Yup.array()
        }
        else {
          switch (attribute.datatype) {
            case DATATYPE.BOOLEAN:
              dynamicSchema[attribute.code] = Yup.boolean()
              break

            case DATATYPE.DATE:
              dynamicSchema[attribute.code] = Yup.date().min(TimeUtil.yesterday(), 'Must today or future date')
              break

            case DATATYPE.PRICE:
              dynamicSchema[attribute.code] = Yup.number().positive('Must be positive number')
              break

            case DATATYPE.INTEGER:
            case DATATYPE.DECIMAL:
              dynamicSchema[attribute.code] = Yup.number().min(0, 'Must not be negative')

              switch (attribute.unit) {
                case UNIT.PERCENTAGE:
                  dynamicSchema[attribute.code] = dynamicSchema[attribute.code].max(100, 'Cannot be more than 100%')
                  break

                default:
                  break
              }
              break

            case DATATYPE.STRING:
              dynamicSchema[attribute.code] = Yup.string()
              break

            default:
              dynamicSchema[attribute.code] = Yup.mixed()
              break
          }
        }
        if (attribute.curation_field_required) dynamicSchema[attribute.code] = dynamicSchema[attribute.code].required('Required')

      })
    }

    return Yup.object().shape({ ...dynamicSchema })
  },

  /**
 * Generate validation attributes schema for Formik.
 * @param {object[]} attributes - Selected Product Attributes.
 * @return {object} Yup validation schema
 */
  buildFormikDatatypeValidationSchmea: (attributes) => {
    let dynamicSchema = {}

    if (attributes) {
      attributes.forEach((attribute) => {
        // If set to array since the formik values would be array for multiple input
        if (attribute.allow_multiple_values) {
          dynamicSchema[attribute.code] = Yup.array()
        }
        else {
          switch (attribute.datatype) {
            case DATATYPE.BOOLEAN:
              dynamicSchema[attribute.code] = Yup.boolean()
              break

            case DATATYPE.DATE:
              dynamicSchema[attribute.code] = Yup.date().min(TimeUtil.yesterday(), 'Must today or future date')
              break

            case DATATYPE.INTEGER:
            case DATATYPE.DECIMAL:
            case DATATYPE.PRICE:
              dynamicSchema[attribute.code] = Yup.number().positive('Must be positive number')

              switch (attribute.unit) {
                case UNIT.PERCENTAGE:
                  dynamicSchema[attribute.code] = dynamicSchema[attribute.code].max(100, 'Cannot be more than 100%')
                  break

                default:
                  break
              }
              break

            case DATATYPE.STRING:
              dynamicSchema[attribute.code] = Yup.string()
              break

            default:
              dynamicSchema[attribute.code] = Yup.mixed()
              break
          }
        }
        if (attribute.curation_field_required) dynamicSchema[attribute.code] = dynamicSchema[attribute.code].required('Required')

      })
    }

    return dynamicSchema
  },

  /**
   * Auto generated name attribute depend on particular attribute values.
   * @param {object} selectedAttributes - selected attribute object. It must follow this structure:
   * @example
   * const selectedAttributes = {
   *    [CODE.BRAND]    : 1,
   *    [CODE.COLOUR]   : [2, 3],
   *    [CODE.PATTERN]  : 3,
   *    [...]           : '4' # can be string
   * }
   * @return {string} name value
   */
  autoGeneratedName: (selectedAttributes) => {
    const labels = Object.keys(selectedAttributes).map((key) => {
      let label = ''

      const optionsFromCache = sessionStorage.getItem(key)

      // * as a guard when session storage is undefined
      if (!optionsFromCache) return label

      const jsonOptions = JSON.parse(optionsFromCache)

      switch (key) {
        case CODE.COLOUR: {
          const selectedOptions = jsonOptions.filter((option) => selectedAttributes[key].includes(option.id))

          label = selectedOptions.length > 2
            ? 'Multicolour'
            : selectedOptions.length === 2
              ? `${selectedOptions[0].label} & ${selectedOptions[1].label}`
              : selectedOptions[0]?.label || ''
          break
        }

        default: {
          // * we parse selectedOption to int since some field return id as string value
          const selectedOption = jsonOptions.find((option) => option.id === parseInt(selectedAttributes[key]))
          label                = selectedOption?.label || ''
          break
        }
      }

      return label
    })

    return labels.filter((label) => label !== '').join(' ')
  }
}

export default ProductAttributeService
